/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React from 'react';
import { styled, Box } from '@mui/material';
import { useAppDispatch } from '../store/rootStore';
import { useSelector } from 'react-redux';
import { TestIds } from '../testIds';
import {
  selectContactInfo,
  selectCurrentFarmerAccount,
  selectName,
} from '../features/azure-b2c/storage';
import { PageContainer } from '../components/layout/PageContainer';
import TitleBox from '../components/TitleBox';
import {
  searchOrderHistory,
  selectOrderHistory,
  selectOrderHistoryStatus,
} from '../features/orders/storage/orders.slice';
import { getPastProducts } from '../features/products/storage';
import {
  getSalesAgreements,
  selectActiveSalesAgreements,
  selectAgreementsRequestStatus,
  selectAllSalesAgreements,
} from '../features/sales-agreements/storage';
import { OrdersRequestCache } from '../features/orders/storage/types';
import hashObject from '../util/hash';
import { Loader } from '../components/Loader';
import OrderCard from '../features/orders/components/OrderCard';
import { AgreementsListConatiner } from '../features/sales-agreements/ActiveAgreements';
import SalesAgreementListItem from '../features/sales-agreements/SalesAgreementListItem';
import ContactCard from '../components/ContactCard';
import { TitleWithLink } from '../features/dashboard/components/TitleWithLink';
import { Route, Routes, useNavigate } from 'react-router-dom';
import _ from 'lodash';
import NoSalesAgreement from '../features/sales-agreements/NoSalesAgreement';
import Section from '../components/layout/Section';
import SearchBar from '../features/dashboard/components/SearchBar';
import { NoOrders } from '../features/orders/NoOrders';
import { selectPromoEnabled } from '../features/nutrition-promo/storage';
import { PromoOrderSuccess } from './PromoOrderSuccess';

import { NutritionPromo } from '../features/dashboard/components/promotions/NutritionPromo';
import { DashboardPromotionsAtomic } from '../services/atomic/DashboardPromotionsAtomic';
import { TraderSpecialsBanner } from '../features/dashboard/components/promotions/TraderSpecialsBanner';
import { QuickLinks } from '../features/dashboard/components/QuickLinks';
import { MAX_ORDERS_PAGE_SIZE } from '../features/orders/utils/consts';

const DashboardContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  [theme.breakpoints.up('md')]: {
    gap: '32px',
  },
}));

const ContentWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

const TitleWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '100%',
  flex: 1,
  [theme.breakpoints.up('lg')]: {
    maxWidth: '688px',
  },

  h1: {
    [theme.breakpoints.down('md')]: {
      fontSize: '1.75rem',
    },
  },
}));

const Contents = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  // maxWidth: '100%',
  flex: 1,
  gap: '16px',
  [theme.breakpoints.up('lg')]: {
    // maxWidth: '1180px',
    gap: '32px',
  },
}));

export function Home() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const account = useSelector(selectCurrentFarmerAccount);

  const { hash } = React.useMemo(() => {
    const obj = {
      a: undefined,
      b: undefined,
      c: '',
      d: undefined,
      e: account?.accountNumber,
    };
    return {
      hash: hashObject(obj),
      obj: obj,
    };
  }, [account]);

  const orders = useSelector(selectOrderHistory(hash));
  const salesAgreements = useSelector(selectAllSalesAgreements);
  const activeAgreements = useSelector(selectActiveSalesAgreements);

  const ordersStatus: OrdersRequestCache | null = useSelector(selectOrderHistoryStatus(hash));
  const agreementsStatus = useSelector(selectAgreementsRequestStatus);

  const username = useSelector(selectName);

  const relationshipOwner = useSelector(selectContactInfo);

  const nutritionPromoEnabled = useSelector(selectPromoEnabled);

  const nutritionPromo = React.useMemo(() => {
    return account?.hasCheckoutAccess && nutritionPromoEnabled;
  }, [account, nutritionPromoEnabled]);

  React.useEffect(() => {
    if (account) {
      dispatch(
        searchOrderHistory({
          accountNumber: account.accountNumber,
          skip: 0,
          top: MAX_ORDERS_PAGE_SIZE,
          startDateTime: undefined,
          endDateTime: undefined,
          salesId: '',
          deliveryMode: undefined,
        }),
      );
      dispatch(getSalesAgreements(account.accountNumber));
      dispatch(
        getPastProducts({
          accountNumber: account.accountNumber,
        }),
      );
    }
  }, [account?.accountNumber]);

  const loading = React.useMemo(() => {
    const salesAgreementsToShow = salesAgreements.length === 0 && agreementsStatus !== 'fulfilled';
    const ordersToShow = orders.length === 0 && ordersStatus?.status !== 'fulfilled';
    if (salesAgreementsToShow || ordersToShow) {
      return true;
    }

    return false;
  }, [ordersStatus, agreementsStatus, salesAgreements, orders]);

  const renderOrders = React.useMemo(() => {
    const list = orders.slice(0, 2);
    return (
      <ContentWrapper>
        <TitleWithLink
          title='Recent orders'
          linkTitle='View all orders'
          linkTo={() => navigate('/orders')}
          testId={TestIds.feat.dashboard.list.viewAllOrders}
        />
        {list.length === 0 ? (
          <NoOrders
            bodyText={'You do not have any recent orders.'}
            bodyPropsSx={{ marginBottom: 2 }}
          />
        ) : (
          list.map((x) => <OrderCard data={x} key={x.salesId} />)
        )}
      </ContentWrapper>
    );
  }, [orders]);

  const renderActiveAgreements = React.useMemo(() => {
    if (_.isEmpty(salesAgreements)) return null;
    const list = activeAgreements.slice(0, 2);
    const noActiveAgreements = (
      <NoSalesAgreement body={'You do not have any active Sales Agreements.'} />
    );
    return (
      <ContentWrapper>
        <TitleWithLink
          title='Active sales agreements'
          linkTitle='View all'
          linkTo={() => navigate('/agreements')}
          testId={TestIds.feat.dashboard.list.viewAllAgreements}
        />
        <AgreementsListConatiner
          data-testid={TestIds.feat.agreement.tabView.activeAgreementsList}
          sx={{ gap: 2, margin: 0 }}
        >
          {list.length === 0
            ? noActiveAgreements
            : list.map((e) => (
                <SalesAgreementListItem
                  key={e.id}
                  {...e}
                  products={e.lines.map((p) => p.productDescription)}
                  percentageFulfilled={e.percentageFulfilled}
                />
              ))}
        </AgreementsListConatiner>
      </ContentWrapper>
    );
  }, [activeAgreements, salesAgreements]);

  const renderContactCard = React.useMemo(() => {
    return (
      <ContactCard
        title={`Contact your Farmlands representative if you have any questions about your Farmlands account and FarmlandsPRO. `}
        sx={{ minWidth: '100%' }}
        {...relationshipOwner}
      />
    );
  }, [relationshipOwner]);

  const showDefaultContents = React.useMemo(() => {
    if (orders.length === 0 && _.isEmpty(salesAgreements)) return true;
    return false;
  }, [orders, salesAgreements]);

  const renderHomePage = () => {
    return (
      <PageContainer data-testid={TestIds.pages.home} sx={{ maxWidth: 1080 }}>
        {loading && (
          <Box display='flex' height='100%' width='100%'>
            <Loader />
          </Box>
        )}
        {!loading && (
          <DashboardContainer>
            <Section pb={1}>
              <TitleWrapper>
                <TitleBox display={`Kia Ora ${username}`} />
              </TitleWrapper>
            </Section>

            {/* Controlled by Remote Config */}
            <TraderSpecialsBanner />

            {/* Controlled by an old Server based Remote Config: Could be deleted?? May never use again */}
            <NutritionPromo />

            <Box display='flex' gap={2}>
              <Contents>
                <SearchBar />
                <QuickLinks />
                <DashboardPromotionsAtomic />
                {!showDefaultContents && (
                  <>
                    {renderOrders}
                    {renderActiveAgreements}
                  </>
                )}
                {renderContactCard}
              </Contents>
            </Box>
          </DashboardContainer>
        )}
      </PageContainer>
    );
  };

  return (
    <Routes>
      <Route path='/' element={renderHomePage()} />
      {nutritionPromo && <Route path='/promo-order-success' element={<PromoOrderSuccess />} />}
    </Routes>
  );
}
export default Home;
